import { type ComponentPropsWithoutRef, type ReactElement, useState } from 'react';
import cx from 'clsx';
import styles from './avatar.module.css';
import { getBackgroundColor } from './helpers/get-background-color';
import { type AvatarBackgroundColor, type AvatarSize, getIconSizeForAvatarSize } from './config';
import { ContentRenderer } from './content-renderer';
import { AvatarIconOverlay } from './avatar-icon-overlay';

interface AvatarProps extends Omit<ComponentPropsWithoutRef<'img'>, 'content'> {
  backgroundColor?: AvatarBackgroundColor;
  content?: ReactElement;
  iconAltValue?: string;
  iconElement?: ReactElement;
  iconTestId?: string;
  isDisabled?: boolean;
  name?: string;
  size?: AvatarSize;
  withBorder?: boolean;
}

export function Avatar({
  backgroundColor,
  content,
  iconAltValue,
  iconElement,
  iconTestId = '',
  isDisabled = false,
  name = '',
  size = '32',
  withBorder = false,
  ...props
}: AvatarProps): ReactElement {
  const [imageError, setImageError] = useState(Boolean(!props.src));

  const hasName = Boolean(name.trim());
  const showInitials = hasName && imageError;

  const color = getBackgroundColor(name, backgroundColor);
  const backgroundColorClass = styles[`avatar-initials-color-${color}`];

  const iconSize = !iconElement ? null : getIconSizeForAvatarSize(size);

  return (
    <div
      className={cx(
        styles['avatar-wrapper'],
        styles[`size-${size}`],
        imageError && hasName && styles['avatar-initials'],
        imageError && hasName && backgroundColorClass,
        withBorder && styles['avatar-borders'],
        isDisabled && styles['avatar-disabled']
      )}
      data-test-avatar-wrapper
    >
      <ContentRenderer
        content={content}
        name={name}
        props={props}
        setImageError={setImageError}
        showInitials={showInitials}
        size={size}
      />
      {iconElement && iconSize ? (
        <AvatarIconOverlay iconAltValue={iconAltValue} iconSize={iconSize} iconTestId={iconTestId}>
          {iconElement}
        </AvatarIconOverlay>
      ) : null}
    </div>
  );
}
